// Whitelist-only safe expression evaluator — never uses eval().
// Supports: ==, !=, >, <, >=, <=, &&, ||, !
// Operand forms: $.assets['id'], $.objectives['id'], $.elapsed, arg:<n>, string/number literals

export function evaluate(expression, state, args) {
  try {
    return !!evalExpr(expression.trim(), state, args);
  } catch {
    return false;
  }
}

function evalExpr(expr, state, args) {
  expr = expr.trim();
  const stripped = stripOuterParens(expr);
  if (stripped !== expr) return evalExpr(stripped, state, args);

  const orParts = splitByOperator(expr, "||");
  if (orParts) return evalExpr(orParts[0], state, args) || evalExpr(orParts[1], state, args);

  const andParts = splitByOperator(expr, "&&");
  if (andParts) return evalExpr(andParts[0], state, args) && evalExpr(andParts[1], state, args);

  for (const op of ["==", "!=", ">=", "<=", ">", "<"]) {
    const cmpParts = splitByOperator(expr, op);
    if (cmpParts) {
      const left = resolveOperand(cmpParts[0].trim(), state, args);
      const right = resolveOperand(cmpParts[1].trim(), state, args);
      return compare(left, right, op);
    }
  }

  if (expr.startsWith("!")) return !evalExpr(expr.slice(1).trim(), state, args);

  return resolveOperand(expr, state, args);
}

function resolveOperand(token, state, args) {
  if (token === "$.elapsed") return state.elapsed;

  const assetMatch = token.match(/^\$\.assets\[['"](.+)['"]\]$/);
  if (assetMatch) return state.assets[assetMatch[1]] ?? "";

  const objMatch = token.match(/^\$\.objectives\[['"](.+)['"]\]$/);
  if (objMatch) return state.objectives[objMatch[1]] ?? "";

  const argMatch = token.match(/^arg:(\d+)$/);
  if (argMatch) return args?.[parseInt(argMatch[1], 10)] ?? "";

  const strMatch = token.match(/^['"](.*)['"]$/);
  if (strMatch) return strMatch[1];

  const num = Number(token);
  if (!Number.isNaN(num)) return num;

  if (token === "true") return true;
  if (token === "false") return false;

  throw new Error(`Unknown operand: ${token}`);
}

function compare(left, right, op) {
  switch (op) {
    case "==":  return left === right;
    case "!=":  return left !== right;
    case ">":   return left > right;
    case "<":   return left < right;
    case ">=":  return left >= right;
    case "<=":  return left <= right;
    default:    return false;
  }
}

function splitByOperator(expr, op) {
  let depth = 0;
  for (let i = 0; i <= expr.length - op.length; i++) {
    const ch = expr[i];
    if (ch === "(" || ch === "[") depth++;
    else if (ch === ")" || ch === "]") depth--;
    else if (depth === 0 && expr.slice(i, i + op.length) === op) {
      return [expr.slice(0, i), expr.slice(i + op.length)];
    }
  }
  return null;
}

function stripOuterParens(expr) {
  if (expr[0] !== "(" || expr[expr.length - 1] !== ")") return expr;
  let depth = 0;
  for (let i = 0; i < expr.length; i++) {
    if (expr[i] === "(") depth++;
    else if (expr[i] === ")") {
      depth--;
      if (depth === 0 && i < expr.length - 1) return expr;
    }
  }
  return expr.slice(1, -1);
}
